import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import {
  LayoutDashboard,
  Calendar,
  Users,
  Receipt,
  LogOut,
  Menu,
  X,
  CreditCard,
  Settings as SettingsIcon,
  UsersRound,
  Clock,
  BarChart3,
  ListOrdered,
  Search,
  ChevronUp,
} from "lucide-react";
import { useState, useEffect } from "react";
import SearchPalette from "./SearchPalette";
import FloatingActionButton from "./FloatingActionButton";

const ALL_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", roles: ["admin", "doctor", "receptionist"] },
  { to: "/appointments", label: "Appointments", icon: Calendar, testid: "nav-appointments", roles: ["admin", "doctor", "receptionist"] },
  { to: "/queue", label: "Queue", icon: ListOrdered, testid: "nav-queue", roles: ["admin", "doctor", "receptionist"] },
  { to: "/patients", label: "Patients", icon: Users, testid: "nav-patients", roles: ["admin", "doctor", "receptionist"] },
  { to: "/schedules", label: "Schedules", icon: Clock, testid: "nav-schedules", roles: ["admin", "doctor"] },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports", roles: ["admin", "doctor"] },
  { to: "/billing", label: "Billing", icon: Receipt, testid: "nav-billing", roles: ["admin", "doctor", "receptionist"] },
];

// Account-level links, shown inside the expandable profile menu at the bottom of the sidebar
const PROFILE_NAV = [
  { to: "/team", label: "Team", icon: UsersRound, testid: "nav-team", roles: ["admin", "doctor"] },
  { to: "/billing/upgrade", label: "Subscription", icon: CreditCard, testid: "nav-subscription", roles: ["admin", "doctor"] },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings", roles: ["admin", "doctor"] },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const navItems = ALL_NAV.filter((n) => !user?.role || n.roles.includes(user.role));
  const profileItems = PROFILE_NAV.filter((n) => !user?.role || n.roles.includes(user.role));

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Global Cmd/Ctrl+K shortcut to open search
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === "Escape") setSearchOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const Sidebar = (
    <aside
      className="w-64 bg-white border-r border-slate-200 flex flex-col h-screen max-h-screen overflow-hidden sticky top-0"
      data-testid="app-sidebar"
    >
      <div className="px-6 py-6 border-b border-slate-200 flex items-center gap-2.5" data-testid="sidebar-brand">
        <img
          src="/logo.jpeg"
          alt="HeDesk"
          className="w-10 h-10 rounded-[22%] object-cover"
        />
        <div>
          <div className="font-bold text-slate-900 text-lg leading-tight tracking-tight" style={{ fontFamily: "Manrope" }}>
            HeDesk
          </div>
          <div className="text-xs text-slate-500">Practice Manager</div>
        </div>
      </div>

      <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-1">
        {/* Global search trigger */}
        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          className="w-full flex items-center gap-3 px-3 py-2 mb-2 rounded-lg text-sm text-slate-500 bg-slate-50 hover:bg-slate-100 transition-colors border border-slate-200"
          data-testid="global-search-trigger"
        >
          <Search className="w-4 h-4" />
          <span className="flex-1 text-left">Search…</span>
          <kbd className="hidden lg:inline-flex items-center gap-0.5 text-[10px] font-mono text-slate-400 border border-slate-200 rounded px-1 py-0.5 bg-white">⌘K</kbd>
        </button>
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={item.testid}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                isActive
                  ? "bg-blue-50 text-blue-700"
                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              }`
            }
          >
            <item.icon className="w-5 h-5" strokeWidth={1.75} />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="shrink-0 p-3 border-t border-slate-200 bg-white">
        {profileOpen && (
          <div className="mb-2 space-y-1 max-h-[45vh] overflow-y-auto" data-testid="profile-menu">
            {profileItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                onClick={() => {
                  setMobileOpen(false);
                  setProfileOpen(false);
                }}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    isActive
                      ? "bg-blue-50 text-blue-700"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`
                }
              >
                <item.icon className="w-5 h-5" strokeWidth={1.75} />
                {item.label}
              </NavLink>
            ))}
            <button
              type="button"
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" strokeWidth={1.75} />
              Logout
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={() => setProfileOpen((v) => !v)}
          aria-expanded={profileOpen}
          className={`w-full flex items-center gap-3 px-2 py-2 rounded-lg transition-colors text-left ${
            profileOpen ? "bg-slate-100" : "hover:bg-slate-50"
          }`}
          data-testid="profile-toggle"
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-sky-500 flex items-center justify-center text-white font-semibold shrink-0">
            {user?.name?.charAt(0)?.toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate" data-testid="sidebar-user-name">
              {user?.name}
            </div>
            <div className="text-xs text-slate-500 capitalize">{user?.role}</div>
          </div>
          <ChevronUp
            className={`w-4 h-4 text-slate-400 shrink-0 transition-transform duration-200 ${
              profileOpen ? "" : "rotate-180"
            }`}
          />
        </button>
      </div>
    </aside>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Mobile header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white font-bold text-sm"
            style={{ fontFamily: "Manrope" }}
          >
            H
          </div>
          <span className="font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Manrope" }}>
            HeDesk
          </span>
        </div>
        <Button variant="ghost" size="icon" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden md:block">{Sidebar}</div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-30 bg-black/40" onClick={() => setMobileOpen(false)}>
          <div className="absolute left-0 top-0 h-full" onClick={(e) => e.stopPropagation()}>
            {Sidebar}
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 min-w-0 md:pt-0 pt-16">{children}</main>

      <SearchPalette open={searchOpen} onOpenChange={setSearchOpen} />
      <FloatingActionButton />
    </div>
  );
}
